import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Ticket, HelpCircle, Grid3x3 } from "lucide-react";
import { Link } from "react-router-dom";

export default function Games() {
  const games = [
    {
      title: "Digital Raffle",
      description: "Create raffles with automated draws and 30-second animation",
      icon: Ticket,
      features: ["License confirmation", "Parents buy tickets online", "Email results", "Fundraising page display"],
      path: "/games/raffle"
    },
    {
      title: "Guessing Game",
      description: "Weight, name, or quantity guessing games with prizes",
      icon: HelpCircle,
      features: ["Image upload", "No duplicate guesses", "Auto/manual winner", "Email results"],
      path: "/games/guessing"
    },
    {
      title: "Bingo Caller",
      description: "Interactive bingo with audio calls and animations",
      icon: Grid3x3,
      features: ["Audio phrases (legs eleven)", "QR code for players", "Speed control", "Auto/manual play"],
      path: "/games/bingo"
    },
  ];

  return (
    <div className="space-y-6 animate-fade-in">
      <div>
        <h1 className="text-3xl font-bold text-foreground">PTA Games</h1>
        <p className="text-muted-foreground mt-2">
          Interactive games to engage your community and raise funds
        </p>
        <Badge className="mt-2 bg-primary">Standard Plan Only</Badge>
      </div>

      <div className="p-4 bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-lg">
        <p className="text-sm text-blue-800 dark:text-blue-200">
          <strong>Premium Feature:</strong> Games are available exclusively on the Standard plan (£129.99/year). 
          Upgrade to unlock all game features and boost engagement at your events.
        </p>
      </div>

      <div className="grid md:grid-cols-3 gap-6">
        {games.map((game) => {
          const Icon = game.icon;
          return (
            <Card key={game.title} className="shadow-card hover:shadow-elevated transition-shadow">
              <CardHeader>
                <div className="flex items-center gap-3 mb-2">
                  <div className="p-3 rounded-lg bg-gradient-primary">
                    <Icon className="h-6 w-6 text-primary-foreground" />
                  </div>
                  <CardTitle className="text-xl">{game.title}</CardTitle>
                </div>
                <CardDescription>{game.description}</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div>
                    <h4 className="font-semibold text-sm mb-2">Features:</h4>
                    <ul className="space-y-1">
                      {game.features.map((feature, i) => (
                        <li key={i} className="text-sm text-muted-foreground flex items-start gap-2">
                          <span className="text-primary mt-1">•</span>
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <Link to={game.path}>
                    <Button className="w-full">Launch Game</Button>
                  </Link>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Quick Start Guide */}
      <Card className="shadow-card">
        <CardHeader>
          <CardTitle>Getting Started with Games</CardTitle>
          <CardDescription>Tips for running successful game-based fundraisers</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-3 gap-6">
            <div>
              <h4 className="font-semibold mb-2">1. Choose Your Game</h4>
              <p className="text-sm text-muted-foreground">
                Select the game that best fits your event. Raffles work great for all events, 
                guessing games add interactivity, and bingo is perfect for evening entertainment.
              </p>
            </div>
            <div>
              <h4 className="font-semibold mb-2">2. Set Up & Promote</h4>
              <p className="text-sm text-muted-foreground">
                Use the wizard to configure your game, then share the link or QR code with parents. 
                Promote through your fundraising page and social media.
              </p>
            </div>
            <div>
              <h4 className="font-semibold mb-2">3. Run & Celebrate</h4>
              <p className="text-sm text-muted-foreground">
                Let the system handle draws and winner selection. Results are automatically emailed 
                and displayed on your public fundraising page.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
